import { useState } from "react";
import { useLocation } from "react-router-dom";
import { IconBell, IconBellOff, IconDeviceMobileDown, IconShare2, IconSquarePlus, IconX } from "@tabler/icons-react";
import { useI18n } from "../i18n";
import { usePwa } from "../lib/pwa";
import { Alert, Button, IconButton, ListRow, Sheet, useToast } from "./ui";

/** iOS has no install prompt: Share → Add to Home Screen, explained step by step. */
function IosSteps() {
  const { t } = useI18n();
  return (
    <ol className="flex flex-col gap-4 text-sm text-ink">
      <li className="flex items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sunken text-info">
          <IconShare2 size={20} />
        </span>
        {t("iosStep1")}
      </li>
      <li className="flex items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sunken text-ink">
          <IconSquarePlus size={20} />
        </span>
        {t("iosStep2")}
      </li>
      <li className="flex items-center gap-3">
        <img src="/apple-touch-icon.png" alt="" className="h-10 w-10 shrink-0 rounded-xl" />
        {t("iosStep3")}
      </li>
    </ol>
  );
}

/** Install the app: the browser's own dialog where there is one, instructions on iOS. */
export function useInstall() {
  const pwa = usePwa();
  const [iosOpen, setIosOpen] = useState(false);
  const available = pwa.canInstall || pwa.iosInstall;
  const start = () => (pwa.canInstall ? void pwa.install() : setIosOpen(true));
  return { available, start, iosOpen, closeIos: () => setIosOpen(false) };
}

export function IosInstallSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useI18n();
  return (
    <Sheet open={open} onClose={onClose} title={t("installTitle")} footer={<Button block onClick={onClose}>{t("gotIt")}</Button>}>
      <p className="mb-5 text-sm text-muted">{t("installBody")}</p>
      <IosSteps />
    </Sheet>
  );
}

/** A dismissible card on Home inviting the agent to install. */
export function InstallBanner() {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const { installDismissed, dismissInstall, standalone } = usePwa();
  const install = useInstall();
  if (pathname !== "/" || standalone || installDismissed || !install.available) return null;
  return (
    <>
      <div className="mb-5 flex items-center gap-3 rounded-2xl border border-line bg-surface p-3 pr-2 shadow-card">
        <img src="/icon-192.png" alt="" className="h-12 w-12 shrink-0 rounded-xl" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-ink">{t("installTitle")}</p>
          <p className="text-xs text-muted">{t("installShort")}</p>
        </div>
        <Button size="sm" onClick={install.start}>
          {t("install")}
        </Button>
        <IconButton label={t("close")} onClick={dismissInstall} className="h-9 w-9 text-muted">
          <IconX size={18} />
        </IconButton>
      </div>
      <IosInstallSheet open={install.iosOpen} onClose={install.closeIos} />
    </>
  );
}

/** A menu row for More / Help. Hidden once installed. */
export function InstallRow() {
  const { t } = useI18n();
  const install = useInstall();
  if (!install.available) return null;
  return (
    <>
      <ListRow icon={IconDeviceMobileDown} title={t("installTitle")} subtitle={t("installShort")} onClick={install.start} />
      <IosInstallSheet open={install.iosOpen} onClose={install.closeIos} />
    </>
  );
}

/** Push notification switch, with the reason when it can't be turned on. */
export function PushSettings({ compact }: { compact?: boolean }) {
  const { t } = useI18n();
  const toast = useToast();
  const pwa = usePwa();
  const install = useInstall();
  const [error, setError] = useState(false);

  // iOS only allows web push from the installed app.
  if (pwa.push === "unsupported") {
    if (compact) return null;
    return pwa.iosInstall ? (
      <>
        <Alert tone="info" action={<Button size="sm" variant="secondary" onClick={install.start}>{t("install")}</Button>}>
          {t("pushIosInstall")}
        </Alert>
        <IosInstallSheet open={install.iosOpen} onClose={install.closeIos} />
      </>
    ) : (
      <p className="text-sm text-muted">{t("pushUnsupported")}</p>
    );
  }
  if (pwa.push === "unavailable") return compact ? null : <p className="text-sm text-muted">{t("pushUnavailable")}</p>;
  if (pwa.push === "denied") return <Alert tone="warning">{t("pushDenied")}</Alert>;
  if (compact && pwa.push === "on") return null;

  const on = pwa.push === "on";
  return (
    <div className={compact ? "flex items-center gap-3 rounded-2xl border border-line bg-surface p-4 shadow-card" : "flex items-center gap-3"}>
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${on ? "bg-success-soft text-success" : "bg-primary-soft text-brand"}`}>
        {on ? <IconBell size={20} /> : <IconBellOff size={20} />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-ink">{on ? t("pushOn") : t("pushOffTitle")}</p>
        <p className="text-xs text-muted">{error ? t("somethingWrong") : on ? t("pushOnBody") : t("pushOffBody")}</p>
      </div>
      <Button
        size="sm"
        variant={on ? "secondary" : "primary"}
        loading={pwa.pushBusy}
        onClick={async () => {
          setError(false);
          try {
            if (on) {
              await pwa.disablePush();
              toast(t("pushTurnedOff"), "info");
            } else {
              const r = await pwa.enablePush();
              if (r === "on") toast(t("pushTurnedOn"));
            }
          } catch {
            setError(true);
          }
        }}
      >
        {on ? t("turnOff") : t("turnOn")}
      </Button>
    </div>
  );
}
